// src/pages/languages/LanguageCategoriesList.jsx
import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  FolderOpen,
  Edit2,
  Trash2,
  ArrowRight,
  BookOpen,
  Layers,
  CreditCard,
  Sparkles,
} from "lucide-react";
import { languageCategoriesAPI } from "../../services/languageService";

export default function LanguageCategoriesList() {
  const { languageId } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [languageInfo, setLanguageInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageId]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await languageCategoriesAPI.getAll(languageId);
      setCategories(data.categories || []);
      setLanguageInfo(data.language || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${category.name}"؟`)) return;
    try {
      await languageCategoriesAPI.delete(category.id);
      setCategories((prev) => prev.filter((c) => c.id !== category.id));
    } catch {
      alert("حدث خطأ أثناء الحذف");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <button
            onClick={() => navigate("/dashboard/languages")}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowRight className="w-4 h-4" />
            رجوع للغات
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            كاتيجوريز {languageInfo?.name ? `— ${languageInfo.name}` : ""}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            إدارة الكاتيجوريز الخاصة بهذه اللغة
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to={`/dashboard/languages/${languageId}/ai`}
            className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all font-bold shadow-lg shadow-violet-200 hover:shadow-violet-300"
          >
            <Sparkles className="w-5 h-5" />
            <span>AI Generation</span>
          </Link>
          <Link
            to={`/dashboard/languages/${languageId}/categories/create`}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>إضافة كاتيجوري</span>
          </Link>
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="card text-center py-16">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            لا توجد كاتيجوريز
          </h3>
          <p className="text-gray-500 text-sm mb-6">ابدأ بإضافة كاتيجوري جديد</p>
          <Link
            to={`/dashboard/languages/${languageId}/categories/create`}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            إضافة كاتيجوري
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {categories.map((cat) => (
            <div key={cat.id} className="card hover:shadow-lg transition-shadow">
              <div className="bg-purple-50 rounded-xl p-4 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">{cat.name}</p>
                  {cat.instructor_name && (
                    <p className="text-xs font-medium text-purple-600">
                      {cat.instructor_name}
                    </p>
                  )}
                </div>
              </div>

              {cat.description && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {cat.description}
                </p>
              )}

              <div className="flex items-center justify-between text-sm mb-4">
                <span className="text-gray-500 flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5" />
                  {cat.skills_count ?? 0} مهارة
                </span>
                <span className="font-bold text-purple-600 flex items-center gap-1">
                  <BookOpen className="w-3.5 h-3.5" />
                  {cat.total_questions ?? 0}
                </span>
              </div>

              <div className="flex gap-2">
                <Link
                  to={`/dashboard/languages/categories/${cat.id}/skills`}
                  className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition-colors"
                >
                  عرض المهارات
                </Link>
                <Link
                  to={`/dashboard/languages/categories/${cat.id}/plans`}
                  className="flex items-center justify-center p-2 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-600 transition-colors"
                  title="خطط الاشتراك"
                >
                  <CreditCard className="w-4 h-4" />
                </Link>
                <Link
                  to={`/dashboard/languages/categories/${cat.id}/edit`}
                  className="flex items-center justify-center p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => handleDelete(cat)}
                  className="flex items-center justify-center p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}